import axios from 'axios';
import { API } from '../actions/types';
import { accessDenied, apiEnd, apiError, apiStart } from '../actions/api';
import apiConfig from '../config/api';
import TokenStorage from '../lib/utils/token_storage';

/*
 * Every request in the app is an { type: 'API', payload } action built by a
 * request class; nothing calls axios directly. That is what keeps the base URL,
 * the auth header, the loading labels and the error handling in one file
 * instead of at 40 call sites.
 *
 * The client and its interceptor are built once at module load. The glazewave
 * version builds them inside the middleware body, which registers a fresh
 * interceptor on every single request -- by the hundredth request one response
 * runs a hundred handlers.
 */
const client = axios.create({
  baseURL: apiConfig.host + apiConfig.port,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((cfg) => {
  const token = TokenStorage.getAccessToken();

  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }

  return cfg;
});

const apiMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    if (action.type !== API) return undefined;

    const { url, method, data, onSuccess, onFailure, label } = action.payload;

    // GET and DELETE carry their payload in the query string, everything else
    // in the body. A body on a GET is dropped without complaint.
    const dataOrParams = ['GET', 'DELETE'].includes(method) ? 'params' : 'data';

    if (label) {
      dispatch(apiStart(label));
    }

    return client
      .request({ url, method, [dataOrParams]: data })
      .then(({ data: body }) => {
        // rbmowatt/base wraps every response in an envelope: the payload is
        // body.data and body.meta holds the pagination block. Handing the whole
        // envelope to a reducer is what shows up as a page of undefined names.
        dispatch(onSuccess(body.data, body.meta));
      })
      .catch((error) => {
        dispatch(apiError(error));
        dispatch(onFailure(error));

        if (error.response && error.response.status === 403) {
          dispatch(accessDenied(window.location.pathname));
        }
      })
      .finally(() => {
        if (label) {
          dispatch(apiEnd(label));
        }
      });
  };

export default apiMiddleware;
